import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { idempotent } from '@/shared/api/headers';
import { uploadFile } from '@/shared/api/upload';
import { attachFiles } from '@/shared/files';

import { collectionKeys } from './keys';

/** Finance mutations are never optimistic; everything under collections refetches after success. */
function useFinanceMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: collectionKeys.all });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['deals', 'detail'] });
    },
  });
}

export const useCreateInvoice = () =>
  useFinanceMutation((body: Schema<'InvoiceCreate'>) => unwrap(api.POST('/invoices', { body, params: { header: idempotent() } })));

/**
 * Attaches evidence to a money record that was just created. Never throws: the record exists either way and must not
 * be created twice by a retry — `false` tells the caller to say the file can be attached from the record.
 */
async function attachAfterCreate(ownerType: 'payment' | 'revenue_entry', ownerId: string, file: File | null | undefined): Promise<boolean> {
  if (!file) return true;
  try {
    await attachFiles(ownerType, ownerId, [file]);
    return true;
  } catch {
    return false;
  }
}

/** Records a payment; the bank's remittance advice (optional) is attached to it afterwards. */
export const useRecordPayment = () =>
  useFinanceMutation(async ({ body, file }: { body: Schema<'PaymentCreate'>; file?: File | null }) => {
    const payment = await unwrap(api.POST('/payments', { body, params: { header: idempotent() } }));
    return { payment, attached: await attachAfterCreate('payment', payment.id, file) };
  });

export const useAllocatePayment = (paymentId: string) =>
  useFinanceMutation(({ version, body }: { version: string; body: Schema<'AllocationRequest'> }) =>
    unwrap(api.PUT('/payments/{paymentId}/allocations', { params: { path: { paymentId }, header: { 'If-Match': version } }, body })),
  );

/** Statement upload: the file goes to storage as a `recon_statement` upload, then the run is created from its uploadId. */
export const useImportStatement = () =>
  useFinanceMutation(async ({ bank, period, file }: { bank: string; period: string; file: File }) => {
    const uploadId = await uploadFile(file, () => undefined, undefined, 'recon_statement');
    return unwrap(api.POST('/reconciliation/runs', { params: { header: idempotent() }, body: { bank, period, uploadId } }));
  });

export const useCreditNote = (invoiceId: string) =>
  useFinanceMutation((body: Schema<'CreditNoteCreate'>) => unwrap(api.POST('/invoices/{invoiceId}/credit-notes', { params: { path: { invoiceId }, header: idempotent() }, body })));

export const useCancelInvoice = (invoiceId: string) =>
  useFinanceMutation((body: Schema<'InvoiceCancel'>) => unwrap(api.POST('/invoices/{invoiceId}/cancel', { params: { path: { invoiceId }, header: idempotent() }, body })));

export const useVoidPayment = (paymentId: string) =>
  useFinanceMutation(({ version, reason }: { version: string; reason: string }) =>
    unwrap(api.POST('/payments/{paymentId}/void', { params: { path: { paymentId }, header: { 'If-Match': version, ...idempotent() } }, body: { reason } })),
  );

export const useResolveLine = (runId: string) =>
  useFinanceMutation(({ lineId, resolution, note }: { lineId: string; resolution: Schema<'ReconResolution'>; note?: string }) =>
    unwrap(api.POST('/reconciliation/runs/{runId}/lines/{lineId}/resolve', { params: { path: { runId, lineId } }, body: { resolution, ...(note ? { note } : {}) } })),
  );

export const usePostRun = (runId: string) => useFinanceMutation(() => unwrap(api.POST('/reconciliation/runs/{runId}/post', { params: { path: { runId }, header: idempotent() } })));

/** Posts an adjustment or a clawback; its evidence (optional) is attached to the new entry afterwards. */
export const useCreateEntry = (kind: 'adjustment' | 'clawback') =>
  useFinanceMutation(async ({ body, file }: { body: Schema<'EntryCreate'>; file?: File | null }) => {
    const entry = kind === 'adjustment' ? await unwrap(api.POST('/adjustments', { body, params: { header: idempotent() } })) : await unwrap(api.POST('/clawbacks', { body, params: { header: idempotent() } }));
    return { entry, attached: await attachAfterCreate('revenue_entry', entry.id, file) };
  });

/** Closes a bank's month (final: closed months never reopen; late corrections post to the next open month). */
export const useClosePeriod = () =>
  useFinanceMutation(({ period, bankCode }: { period: string; bankCode: string }) =>
    unwrap(api.POST('/collections/periods/{period}/close', { params: { path: { period }, header: idempotent() }, body: { bankCode } })),
  );
